import React from "react";

/**
 * App-wide error boundary.
 *
 * Without this, any uncaught exception thrown during render unmounts the entire
 * React tree and the user sees a blank white screen (there was previously no
 * boundary anywhere in the app). This catches such errors, shows a recoverable
 * fallback, and logs the error + component stack so the real trigger is visible
 * — including on environments we can't reproduce locally (e.g. Win7/Chrome 109).
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
    this.handleReload = this.handleReload.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Surface the real error + component stack. Replace/extend with a remote
    // reporting call if/when one is available.
    console.error(
      "ErrorBoundary caught a render error:",
      error,
      errorInfo?.componentStack,
    );
  }

  handleReload() {
    // Clear the error state; a full reload is the safest recovery for a broken
    // render tree.
    window.location.reload();
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.props.fallback) {
      return this.props.fallback;
    }

    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "16px",
          padding: "24px",
          textAlign: "center",
          fontFamily: "sans-serif",
        }}
      >
        <h2 style={{ margin: 0 }}>Something went wrong</h2>
        <p style={{ margin: 0, color: "#555", maxWidth: "420px" }}>
          The page ran into an unexpected error. Please reload and try again.
        </p>
        <button
          type="button"
          onClick={this.handleReload}
          style={{
            padding: "10px 20px",
            fontSize: "15px",
            borderRadius: "8px",
            border: "none",
            background: "#1976d2",
            color: "#fff",
            cursor: "pointer",
          }}
        >
          Reload
        </button>
      </div>
    );
  }
}

export default ErrorBoundary;
